import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  IconButton,
  Chip,
  Collapse
} from '@mui/material';
import {
  AutoAwesome,
  Add,
  ExpandMore,
  ExpandLess,
  LocalGroceryStore,
  TipsAndUpdates,
  Launch
} from '@mui/icons-material';
import { getAmazonSearchUrl, AMAZON_DISCLOSURE } from '../utils/amazonHelper';
import { getSuggestedItems, getBurtMotivation, getBudgetAdvice } from '../utils/shoppingAI';

function BurtSuggestions({ currentList, onAddItem, currentTotal, budget }) {
  const [suggestions, setSuggestions] = useState([]);
  const [motivation, setMotivation] = useState('');
  const [budgetAdvice, setBudgetAdvice] = useState(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    // Get suggestions
    const newSuggestions = getSuggestedItems([], currentList);
    setSuggestions(newSuggestions);

    // Get motivation
    setMotivation(getBurtMotivation());

    // Get budget advice
    const advice = getBudgetAdvice(currentTotal, budget);
    setBudgetAdvice(advice);
  }, [currentList, currentTotal, budget]);

  const handleAddSuggestion = (suggestion) => {
    onAddItem({
      name: suggestion.name,
      price: suggestion.price,
      category: suggestion.category,
      store: suggestion.store,
      quantity: suggestion.quantity || 1
    });
  };

  const refreshSuggestions = () => {
    const newSuggestions = getSuggestedItems([], currentList);
    setSuggestions(newSuggestions);
    setMotivation(getBurtMotivation());
  };

  return (
    <Card sx={{ mb: 3, bgcolor: 'primary.light', borderLeft: '4px solid', borderColor: 'primary.main' }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              🤖
            </Avatar>
            Burt's Shopping Assistant
          </Typography>
          <IconButton onClick={() => setExpanded(!expanded)} sx={{ minWidth: 48, minHeight: 48 }}>
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          {/* Motivation Message */}
          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 2 }}>
            <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TipsAndUpdates color="info" />
              {motivation}
            </Typography>
          </Box>

          {/* Budget Advice */}
          {budgetAdvice && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                bgcolor: budgetAdvice.status === 'over' ? 'error.light' : budgetAdvice.status === 'warning' ? 'warning.light' : 'success.light',
                borderRadius: 2
              }}
            >
              <Typography variant="body1">
                {budgetAdvice.emoji} {budgetAdvice.message}
              </Typography>
            </Box>
          )}

          {/* Smart Suggestions */}
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">
                💡 Smart Suggestions
              </Typography>
              <Button
                size="small"
                startIcon={<AutoAwesome />}
                onClick={refreshSuggestions}
              >
                Refresh
              </Button>
            </Box>

            {suggestions.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                Your list looks complete! 🎉
              </Typography>
            ) : (
              <>
                <List dense>
                  {suggestions.map((suggestion, index) => (
                    <ListItem
                      key={index}
                      sx={{
                        bgcolor: 'background.paper',
                        borderRadius: 1,
                        mb: 1,
                        flexWrap: 'wrap'
                      }}
                    >
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: suggestion.store === 'amazon' ? '#FF9900' : '#0071CE' }}>
                          <LocalGroceryStore />
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={suggestion.name}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              ~${suggestion.price.toFixed(2)} • {suggestion.reason}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                              <Chip
                                label={suggestion.category}
                                size="small"
                              />
                              <Chip
                                label={suggestion.store === 'amazon' ? '🟧 Amazon' : '🟦 Walmart'}
                                size="small"
                                sx={{ bgcolor: suggestion.store === 'amazon' ? '#FF990022' : '#0071CE22' }}
                              />
                              {suggestion.store === 'amazon' && (
                                <Chip
                                  label="Shop ↗"
                                  size="small"
                                  clickable
                                  component="a"
                                  href={getAmazonSearchUrl(suggestion.name)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  icon={<Launch sx={{ fontSize: '0.75rem !important' }} />}
                                  sx={{ bgcolor: '#FF990033', cursor: 'pointer' }}
                                />
                              )}
                            </Box>
                          </Box>
                        }
                      />
                      <IconButton
                        edge="end"
                        color="primary"
                        title="Add to list"
                        onClick={() => handleAddSuggestion(suggestion)}
                      >
                        <Add />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
                {/* Affiliate disclosure — required by Amazon Associates agreement */}
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, px: 1 }}>
                  {AMAZON_DISCLOSURE}
                </Typography>
              </>
            )}
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default BurtSuggestions;













